import { createReducer, on } from '@ngrx/store';
import { concat, set, toLower, uniqBy } from 'lodash';
import { QuestionnaireNourishActions } from './nourish.actions';
import { QUESTIONNAIRE_NOURISH_DATA } from './nourish.data';
import {
  QuestionnaireNourishState,
  intialQuestionnaireNourishState,
  questionnaireNourishState
} from './nourish.state';

export const questionnaireNourishReducer = createReducer(
  questionnaireNourishState,
  on(
    QuestionnaireNourishActions.reset,
    (): QuestionnaireNourishState => intialQuestionnaireNourishState()
  ),
  on(
    QuestionnaireNourishActions.setForm,
    (state, { key, value }): QuestionnaireNourishState => ({
      ...state,
      form: {
        ...state.form,
        ...set(state.form, key, value)
      }
    })
  ),
  on(
    QuestionnaireNourishActions.setUserProfile,
    (state, { value: profile }): QuestionnaireNourishState => {
      let form = {
        ...state.form
      };

      if (profile?.information?.height) {
        form = {
          ...form,
          information: {
            ...form.information,
            height: profile.information.height
          }
        };
      }
      if (profile?.information?.weight) {
        form = {
          ...form,
          information: {
            ...form.information,
            weight: profile.information.weight
          }
        };
      }
      if (profile?.lifestyle?.exerciseLevelPerWeek) {
        const found = (
          QUESTIONNAIRE_NOURISH_DATA.exerciseLevelPerWeek?.options ?? []
        ).find(
          (o) =>
            toLower(o.name) ===
            toLower(profile.lifestyle?.exerciseLevelPerWeek ?? '')
        );
        form = {
          ...form,
          exerciseLevelPerWeek: found ?? {
            id: '001',
            name: profile.lifestyle.exerciseLevelPerWeek,
            value: profile.lifestyle.exerciseLevelPerWeek,
            description: profile.lifestyle.exerciseLevelPerWeekCompleted
          }
        };
      }
      if (profile?.medicalHistory && profile.medicalHistory.length > 0) {
        let valueMedicalHistory = {};
        const medicalHistory = uniqBy(
          profile.medicalHistory
            .filter(
              (o) =>
                toLower(o.medicalType) !== 'illness' &&
                toLower(o.medicalType) !== 'surgical'
            )
            .map((item) => ({
              name: item.medicalNameEn || '',
              value: item.medicalNameEn || '',
              medicalType: item.medicalType || 'DISEASE',
              medicalIsOther: item.medicalIsOther || false
            })),
          'name'
        );
        if (medicalHistory.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const medical of medicalHistory) {
            const found = (
              QUESTIONNAIRE_NOURISH_DATA.medicalHistory?.options ?? []
            ).find((o) => toLower(o.name) === toLower(medical.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(medical);
            }
          }
          valueMedicalHistory = {
            medicalHistory: concat(
              selected.length > 1
                ? selected.filter(({ name }) => toLower(name) !== 'none')
                : selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        form = { ...form, ...valueMedicalHistory };
      }
      if ((profile?.allergy ?? []).length > 0) {
        let valueAllergies = {};
        const foodAllergies = (profile?.allergy ?? [])
          .map((o) => ({ ...o, type: o.type ?? 'FOOD' }))
          .filter((o) =>
            Array.isArray(o.type)
              ? (o.type as Array<string>)
                  .map((v) => toLower(v))
                  .includes('food')
              : typeof o.type === 'string' && toLower(o.type) === 'food'
          )
          .map((o) => ({
            name: o.name,
            value: o.name,
            antidote: o.antidote
          }));
        if (foodAllergies.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const allergy of foodAllergies) {
            const found = (
              QUESTIONNAIRE_NOURISH_DATA.allergies?.options ?? []
            ).find((o) => toLower(o.name) === toLower(allergy.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(allergy);
            }
          }
          valueAllergies = {
            ...valueAllergies,
            allergies: concat(
              selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        form = {
          ...form,
          ...valueAllergies
        };
      }
      return {
        ...state,
        form,
        userProfile: profile
      };
    }
  ),
  on(
    QuestionnaireNourishActions.setLoading,
    (state, { key, value }): QuestionnaireNourishState => ({
      ...state,
      loading: {
        ...state.loading,
        [key]: value
      }
    })
  ),
  on(
    QuestionnaireNourishActions.setSubmissionResponse,
    (state, { response, success, message }): QuestionnaireNourishState => ({
      ...state,
      submissionResponse: {
        response,
        success,
        message
      }
    })
  )
);
